import { Edit2, Trash2, X, Download, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { DashboardProduct } from "@/types/dashboard";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { Provider } from "@/types/product";
import { requestExport } from "@/store/sagas/exportSaga";

interface ProductTableProps {
  selectedCompetitors: Provider[];
  onCompetitorRemove: (competitor: Provider) => void;
}

export function ProductTable({ selectedCompetitors, onCompetitorRemove }: ProductTableProps) {
  const dispatch = useAppDispatch();
  const queryLogs = useAppSelector((state) => state.dashboard.queryLogs);
  const isLoading = useAppSelector((state) => state.dashboard.isLoading);
  const isExporting = useAppSelector((state) => state.export.isExporting);
  const selectedBrand = useAppSelector((state) => state.dashboard.selectedBrand);
  const selectedCategory = useAppSelector((state) => state.dashboard.selectedCategory);
  const searchTerm = useAppSelector((state) => state.dashboard.searchTerm);

  const handleExport = () => {
    const params = {
      brand: selectedBrand || undefined,
      category: selectedCategory || undefined,
      search: searchTerm || undefined,
      provider_ids: selectedCompetitors.length > 0 
        ? selectedCompetitors.map(comp => comp.id).join(',')
        : undefined
    };

    dispatch(requestExport({
      isCompetitor: selectedCompetitors.length > 0,
      params
    }));
  };

  const handlePriceClick = (url: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const getCompetitorPrice = (instances: any[], competitorId: string) => {
    const instance = instances?.find(
      inst => inst.provider.id === competitorId
    );
    return instance?.current_price_details.final_price || '-';
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button 
          onClick={handleExport}
          disabled={isExporting || isLoading || queryLogs.length === 0}
          className="gap-2"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Name</TableHead>
              <TableHead>Lowest Price</TableHead>
              <TableHead>Highest Price</TableHead>
              {selectedCompetitors.map((competitor) => (
                <TableHead key={competitor.id} className="pr-12">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{competitor.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => onCompetitorRemove(competitor)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {queryLogs.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={4 + selectedCompetitors.length}
                  className="h-32 text-center text-muted-foreground"
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              queryLogs.map((product) => (
                <TableRow key={product.product_group.id}>
                  <TableCell>
                    <Link
                      to={`/product/${product.product_group.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {product.product_group.title_en}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="link"
                      onClick={() =>
                        handlePriceClick(product.product_group.url || "")
                      }
                    >
                      {product.product_group.min_price}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="link"
                      onClick={() =>
                        handlePriceClick(product.product_group.url || "")
                      }
                    >
                      {product.product_group.max_price}
                    </Button>
                  </TableCell>
                  {selectedCompetitors.map((competitor) => (
                    <TableCell key={competitor.id}>
                      {getCompetitorPrice(product.product_group.instances, competitor.id)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
